import type { Chapter, Storyboard } from './timeline.types';
import type React from 'react';

import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TimelinePreview } from '../../components/timeline-preview/timeline-preview';
import { useCursorPosition } from '../../hooks/use-cursor-position';
import { useInputRangeTouchSupport } from '../../hooks/use-input-range-touch-support';
import { isArrayOfNumbers } from '../../utils/is-array-of-numbers';
import { msToAriaText } from '../../utils/time';

import styles from './timeline.module.css';

type Props = {
  currentTime?: number;
  totalTime?: number;
  min?: number;
  max?: number;
  step?: number;
  bufferPercentage?: number;
  liveUI?: boolean;
  isMobile?: boolean;
  isSmallScreen?: boolean;
  storyboard?: Storyboard;
  onChange?: (value: number) => void;
  chapters?: Chapter[];
};

export function Timeline({
  currentTime = 0,
  totalTime = 100,
  min = 0,
  max = totalTime,
  step = 100,
  bufferPercentage = 0,
  liveUI = false,
  isMobile = false,
  isSmallScreen = false,
  storyboard,
  onChange,
  chapters = [],
}: Props) {
  const ref = useRef(null);
  const { t } = useTranslation();
  const [{ x: hoverPercentage }] = useCursorPosition(ref, true);
  const thumbSize = 18;

  const ariaText = useMemo(
    () =>
      t('ui.timeline.slider.aria-text', {
        currentTime: msToAriaText(t, currentTime),
        totalTime: msToAriaText(t, totalTime),
      }),
    [t, currentTime, totalTime]
  );

  const handleChange = useCallback(
    (value: number) => {
      if (!liveUI) {
        onChange?.(value);
      }
    },
    [liveUI, onChange]
  );

  const adjustedChapters = useMemo(
    () => (chapters.length && chapters[0].start === 0 ? chapters : [{ start: 0 }, ...chapters]),
    [chapters]
  );
  const chaptersModel = useMemo(
    () =>
      adjustedChapters.map((chapter, idx) => {
        const start = chapter.start;
        const end = adjustedChapters[idx + 1]?.start || totalTime;
        const width = ((end - start) / totalTime) * 100;
        const left = (start / totalTime) * 100;

        const currentPosition = currentTime;
        const bufferPosition = bufferPercentage * totalTime;
        const hoverPosition = (hoverPercentage / 100) * totalTime;

        const chapterTimelinePercentage = Math.max(0, Math.min(1, (currentPosition - start) / (end - start))) * 100;
        const chapterBufferPercentage = Math.max(0, Math.min(1, (bufferPosition - start) / (end - start))) * 100;
        const chapterHoverPercentage = Math.max(0, Math.min(1, (hoverPosition - start) / (end - start))) * 100;

        const isHover = hoverPosition >= start && hoverPosition <= end;
        const isCurrent = currentPosition >= start && currentPosition <= end;

        return {
          chapter,
          width,
          left,
          chapterTimelinePercentage,
          chapterBufferPercentage,
          chapterHoverPercentage,
          isHover,
          isCurrent,
        };
      }),
    [adjustedChapters, currentTime, totalTime, bufferPercentage, hoverPercentage]
  );

  const hoveredChapter = useMemo(() => chaptersModel.find(({ isHover }) => isHover)?.chapter, [chaptersModel]);

  const currentChapter = useMemo(() => chaptersModel.find(({ isCurrent }) => isCurrent)?.chapter, [chaptersModel]);

  const inputRangeRef = useRef<HTMLInputElement>(null);

  const inputRangeProps = useInputRangeTouchSupport({
    thumbSize,
    onChange: handleChange,
    currentValue: liveUI ? 1 : currentTime,
    min,
    max: liveUI ? 1 : max,
    step,
  });

  if (!isArrayOfNumbers([min, max, step, currentTime, totalTime])) return null;

  return (
    <div
      ref={ref}
      data-testid="chapters-timeline"
      className={styles.wrapper}
      style={{ '--thumb-size': `${thumbSize}px` } as React.CSSProperties}
    >
      {chaptersModel.map(
        ({ width, left, chapterTimelinePercentage, chapterBufferPercentage, chapterHoverPercentage, isHover }, idx) => (
          <div
            key={idx}
            data-testid={`chapter-segment-${idx}`}
            className={classNames(styles.indicators, { [styles.indicatorsHover]: isHover })}
            style={{ width: `calc(${width}% - 3px)`, left: `calc(${left}% + 1.5px)` }}
          >
            <div data-testid="buffer" style={{ width: `${chapterBufferPercentage}%` }} />
            <div data-testid="hover" style={{ width: `${chapterHoverPercentage}%` }} />
            <div data-testid="progress" style={{ width: `${chapterTimelinePercentage}%` }} />
          </div>
        )
      )}

      <input
        ref={inputRangeRef}
        type="range"
        name="timeline-slider"
        aria-label={t('ui.timeline.slider.label')}
        aria-valuetext={currentChapter?.title ? `${currentChapter.title} ${ariaText}` : ariaText}
        {...inputRangeProps}
        className={classNames(styles.rangeInput, { [styles.animated]: !isMobile }, styles.input)}
      />

      <TimelinePreview
        timelineRef={ref}
        totalTime={totalTime}
        storyboard={storyboard}
        chapter={hoveredChapter}
        hideThumbnail={isSmallScreen}
      />
    </div>
  );
}
